import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, Calendar, Users, AlertTriangle, Plane, Briefcase, Clock } from 'lucide-react';
import ConflictsModal from './ConflictsModal';

const StatusBar = ({
  assignments = [],
  users = [],
  projects = [],
  days = [],
  dayWidth = 60,
  showPeople = true,
  showCars = false,
  showEquipment = false,
  cars = [],
  carAssignments = [],
  trainings = [], // Training assignments
  isSidebarVisible = false,
  currentStatus = null, // Current action like "Dragging assignment", "Editing comment", etc.
  onQuickAction = () => {}, // Callback for quick actions
  onDeleteAssignment = () => {} // Callback for deleting assignments
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showConflictsModal, setShowConflictsModal] = useState(false);

  // Calculate statistics with memoization
  const conflictsData = useMemo(() => {
    return calculateConflicts();
  }, [assignments, trainings, users, projects]);

  const stats = useMemo(() => ({
    totalAssignments: Array.isArray(assignments) ? assignments.length : 0,
    totalUsers: Array.isArray(users) ? users.length : 0,
    totalProjects: Array.isArray(projects) ? projects.length : 0,
    totalDays: Array.isArray(days) ? days.length : 0,
    conflicts: conflictsData.conflicts.length,
    conflictsData: conflictsData.conflicts,
    travels: (Array.isArray(assignments) ? assignments : []).filter(a => (Array.isArray(projects) ? projects : []).find(p => p.id === a.projectId)?.type === 'travel').length,
    utilization: calculateUtilization()
  }), [assignments, users, projects, days, conflictsData]);

  function calculateConflicts() {
    const conflicts = [];
    const userAssignments = {};

    // Filter out travel and non-project assignments
    const regularAssignments = (Array.isArray(assignments) ? assignments : []).filter(assignment => {
      const project = (Array.isArray(projects) ? projects : []).find(p => p.id === assignment.projectId);
      const isRegular = project && project.type !== 'travel' && project.type !== 'holiday' && project.type !== 'day_off';
      return isRegular;
    });
    
    // Group regular assignments by user
    regularAssignments.forEach(assignment => {
      if (!userAssignments[assignment.userId]) {
        userAssignments[assignment.userId] = [];
      }
      userAssignments[assignment.userId].push(assignment);
    });

    // Check for overlaps within each user's assignments
    Object.entries(userAssignments).forEach(([userId, userAssigns]) => {
      const user = (Array.isArray(users) ? users : []).find(u => u.id === parseInt(userId));

      for (let i = 0; i < userAssigns.length; i++) {
        for (let j = i + 1; j < userAssigns.length; j++) {
          const a1 = userAssigns[i];
          const a2 = userAssigns[j];

          const project1 = (Array.isArray(projects) ? projects : []).find(p => p.id === a1.projectId);
          const project2 = (Array.isArray(projects) ? projects : []).find(p => p.id === a2.projectId);
          
          // Check if assignments actually overlap (not just touch)
          const start1 = new Date(a1.startDate);
          const end1 = new Date(a1.endDate);
          const start2 = new Date(a2.startDate);
          const end2 = new Date(a2.endDate);

          // Assignments overlap if start1 < end2 AND start2 < end1
          const hasOverlap = start1 < end2 && start2 < end1;

          if (hasOverlap) {
            const conflict = {
              userId: parseInt(userId),
              assignment1: a1,
              assignment2: a2,
              overlapStart: new Date(Math.max(start1.getTime(), start2.getTime())),
              overlapEnd: new Date(Math.min(end1.getTime(), end2.getTime()))
            };

            conflicts.push(conflict);
          }
        }
      }
    });

    // Check for conflicts between project assignments and trainings
    regularAssignments.forEach(assignment => {
      const user = (Array.isArray(users) ? users : []).find(u => u.id === assignment.userId);

      // Check if this assignment overlaps with any training for the same user
      trainings.forEach(training => {
        // Check if user is assigned to this training
        if (training.users && training.users.includes(assignment.userId)) {
          const assignmentStart = new Date(assignment.startDate);
          const assignmentEnd = new Date(assignment.endDate);
          const trainingStart = new Date(training.start_date);
          const trainingEnd = new Date(training.end_date);

          // Check for overlap: assignments overlap if start1 < end2 AND start2 < end1
          const hasOverlap = assignmentStart < trainingEnd && trainingStart < assignmentEnd;

          if (hasOverlap) {
            const trainingName = training.name ||
              `${training.training_center_qualification?.qualification_type?.qualification_type?.name || 'Training'}`;

            const conflict = {
              userId: assignment.userId,
              assignment1: assignment,
              assignment2: {
                id: `training-${training.id}`,
                userId: assignment.userId,
                projectId: `training-${training.id}`,
                startDate: training.start_date,
                endDate: training.end_date,
                role: 'Training Participant',
                isTraining: true
              },
              overlapStart: new Date(Math.max(assignmentStart.getTime(), trainingStart.getTime())),
              overlapEnd: new Date(Math.min(assignmentEnd.getTime(), trainingEnd.getTime())),
              trainingName: trainingName,
              isProjectTrainingConflict: true
            };

            conflicts.push(conflict);
          }
        }
      });
    });

    return { conflicts };
  }

  function calculateUtilization() {
    const safeUsers = Array.isArray(users) ? users : [];
    const safeDays = Array.isArray(days) ? days : [];
    if (safeUsers.length === 0 || safeDays.length === 0) return 0;
    const totalPossibleDays = safeUsers.length * safeDays.length;
    const assignedDays = (Array.isArray(assignments) ? assignments : []).reduce((total, assignment) => {
      const daysBetween = Math.ceil((assignment.endDate - assignment.startDate) / (1000 * 60 * 60 * 24)) + 1;
      return total + daysBetween;
    }, 0);
    return totalPossibleDays > 0 ? Math.round((assignedDays / totalPossibleDays) * 100) : 0;
  }

  const getDateRange = () => {
    const safeDays = Array.isArray(days) ? days : [];
    if (safeDays.length === 0) return 'No dates';
    const start = safeDays[0];
    const end = safeDays[safeDays.length - 1];

    // Ensure dates are Date objects
    const startDate = start instanceof Date ? start : new Date(start);
    const endDate = end instanceof Date ? end : new Date(end);

    const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  const quickActions = [
    { label: 'Today', action: 'today', icon: Calendar }
  ];

  return (
    <div className="relative">
      {/* Main Status Bar */}
      <div className={`bg-white border-t border-steel-200 shadow-sm transition-all duration-200 ${
        isExpanded ? 'h-auto' : 'h-7'
      }`}>

        {/* Collapsed View */}
        <div className="flex items-center justify-between px-4 h-7 text-xs">
          {/* Left side - Conflicts and Status */}
          <div className="flex items-center space-x-4">
            {stats.conflicts > 0 && (
              <div
                className="flex items-center space-x-1 cursor-pointer hover:bg-red-50 px-1 py-0.5 rounded transition-colors"
                onClick={() => {
                  setShowConflictsModal(true);
                }}
                title="Click to view conflict details"
              >
                <AlertTriangle className="h-3 w-3 text-red-500" />
                <span className="text-red-600 font-medium">{stats.conflicts} conflicts</span>
              </div>
            )}
            {currentStatus && (
              <div className="flex items-center space-x-1 px-2 py-0.5 bg-amber-50 rounded border border-amber-200">
                <Clock className="h-3 w-3 text-amber-500 animate-pulse" />
                <span className="text-amber-700 text-xs font-medium">{currentStatus}</span>
              </div>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side - Toggle only */}
          <div className="flex items-center">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center text-slate-500 hover:text-slate-700 transition-colors"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ?
                <ChevronDown className="h-4 w-4" /> :
                <ChevronUp className="h-4 w-4" />
              }
            </button>
          </div>
        </div>

        {/* Expanded View - Only Daily Summary */}
        {isExpanded && (
          <div className="border-t border-steel-100 bg-steel-50 px-4 py-2">
            {/* Daily Summary Section */}
            {(showPeople || showCars || showEquipment) && (
              <div className="mb-3 border-b border-steel-200 pb-3 -mx-4 px-0">
                <div className="flex">
                  {/* Left Labels Column */}
                  <div className={`${isSidebarVisible ? 'w-48' : 'w-56'} flex-shrink-0`}>
                    {showPeople && (
                      <div className="mb-2">
                        <div className="min-h-16 bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 border-t-2 border-steel-500 border-l-2 border-l-gray-300 border-r border-steel-200 rounded-l pl-2 pr-2 py-1">
                          <div className="text-xs font-bold text-slate-800 mb-1">Personnel Summary</div>
                          {/* Legend - two columns - only items that appear on calendar */}
                          {(() => {
                            // Get unique special tiles from assignments
                            const specialTilesOnCalendar = Array.from(
                              new Map(
                                assignments
                                  .filter(a => a.isSpecialTile || a.is_special_tile)
                                  .map(a => [a.projectName, { name: a.projectName, color: a.hexColor }])
                              ).values()
                            );

                            // Check if there are any trainings with users assigned
                            const hasTrainings = trainings.some(t => t.users && t.users.length > 0);

                            // Get unique projects that have assignments
                            const projectIdsOnCalendar = new Set(
                              assignments
                                .filter(a => !(a.isSpecialTile || a.is_special_tile) && a.projectId)
                                .map(a => a.projectId)
                            );
                            const projectsOnCalendar = projects.filter(p => projectIdsOnCalendar.has(p.id));

                            return (
                              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                <div className="flex items-center gap-1">
                                  <div className="w-3 h-3 rounded bg-steel-300 border border-steel-400 flex-shrink-0"></div>
                                  <span className="text-[10px] text-slate-600 truncate">Total</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: '#22c55e' }}></div>
                                  <span className="text-[10px] text-slate-600 truncate">Available</span>
                                </div>
                                {/* Special tiles that appear on calendar */}
                                {specialTilesOnCalendar.map((item, idx) => (
                                  <div key={`legend-special-${idx}`} className="flex items-center gap-1">
                                    <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: item.color || '#9CA3AF' }}></div>
                                    <span className="text-[10px] text-slate-600 truncate">{item.name || 'Other'}</span>
                                  </div>
                                ))}
                                {/* Training indicator */}
                                {hasTrainings && (
                                  <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: '#22c55e' }}></div>
                                    <span className="text-[10px] text-slate-600 truncate">Training</span>
                                  </div>
                                )}
                                {/* Projects that appear on calendar */}
                                {projectsOnCalendar.map((project, idx) => (
                                  <div key={`legend-project-${idx}`} className="flex items-center gap-1">
                                    <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: project.color || '#6B7280' }}></div>
                                    <span className="text-[10px] text-slate-600 truncate">{project.name}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                    {(showCars || showEquipment) && (
                      <div>
                        <div className="min-h-16 bg-gradient-to-br from-blue-100 via-blue-50 to-blue-100 border-t-2 border-blue-500 border-l-2 border-l-blue-300 border-r border-steel-200 rounded-l flex items-center pl-2 pr-3 py-3">
                          <div>
                            <div className="text-xs font-bold text-brand-800">Cars & Equipment</div>
                            <div className="text-xs text-brand-600 mt-0.5">Vehicles & Equipment by Project</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Data Column */}
                  <div className="flex-1 overflow-x-auto">
                    {showPeople && (
                      <div className="mb-2">
                        <div className="min-h-16 bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 border-t-2 border-steel-500 border-l-2 border-l-gray-300 rounded-r" style={{ width: days.length * dayWidth }}>
                          <div className="relative flex">
                            {days.map((day, dayIndex) => {
                              // Group people by project/special tile for this day
                              const summaryMap = new Map();
                              const usersWithAssignments = new Set();

                              users.forEach(user => {
                                const userAssignmentsForDay = assignments
                                  .filter(a => a.userId === user.id)
                                  .filter(a => {
                                    const assignmentStart = new Date(a.startDate);
                                    const assignmentEnd = new Date(a.endDate);
                                    const currentDay = new Date(day);
                                    assignmentStart.setHours(0, 0, 0, 0);
                                    assignmentEnd.setHours(0, 0, 0, 0);
                                    currentDay.setHours(0, 0, 0, 0);
                                    return currentDay >= assignmentStart && currentDay <= assignmentEnd;
                                  });

                                // Check if user has training on this day
                                const userTrainingsForDay = trainings.filter(training => {
                                  if (!training.users || !training.users.includes(user.id)) return false;
                                  const trainingStart = new Date(training.start_date);
                                  const trainingEnd = new Date(training.end_date);
                                  const currentDay = new Date(day);
                                  trainingStart.setHours(0, 0, 0, 0);
                                  trainingEnd.setHours(0, 0, 0, 0);
                                  currentDay.setHours(0, 0, 0, 0);
                                  return currentDay >= trainingStart && currentDay <= trainingEnd;
                                });

                                if (userAssignmentsForDay.length > 0 || userTrainingsForDay.length > 0) {
                                  usersWithAssignments.add(user.id);
                                }

                                userAssignmentsForDay.forEach(a => {
                                  // Handle special tiles (quick tags like vacation, training)
                                  const isSpecial = a.isSpecialTile || a.is_special_tile;
                                  let key, name, color, sortOrder;

                                  if (isSpecial) {
                                    // Special tile - use projectName and hexColor
                                    key = `special-${a.projectName || 'other'}`;
                                    name = a.projectName || 'Other';
                                    color = a.hexColor || '#9CA3AF';
                                    sortOrder = 1; // Special tiles come after free people
                                  } else {
                                    // Regular project assignment
                                    const project = projects.find(p => p.id === a.projectId);
                                    key = `project-${a.projectId}`;
                                    name = project?.name || 'Unassigned';
                                    color = project?.color || a.hexColor || '#6B7280';
                                    sortOrder = 2; // Projects come last
                                  }

                                  if (!summaryMap.has(key)) {
                                    summaryMap.set(key, { name, color, sortOrder, users: new Set() });
                                  }
                                  summaryMap.get(key).users.add(user.id);
                                });

                                // Add trainings to summary (all trainings grouped together)
                                if (userTrainingsForDay.length > 0) {
                                  const key = 'training';
                                  if (!summaryMap.has(key)) {
                                    summaryMap.set(key, {
                                      name: 'Training',
                                      color: '#22c55e', // Green color for trainings
                                      sortOrder: 1.5, // Between special tiles and projects
                                      users: new Set()
                                    });
                                  }
                                  summaryMap.get(key).users.add(user.id);
                                }
                              });

                              // Calculate free people (no assignments for this day)
                              const freeCount = users.length - usersWithAssignments.size;

                              const isSunday = day.getDay() === 0;
                              const isToday = day.toDateString() === new Date().toDateString();

                              // Sort entries: free people (0) -> special tiles (1) -> projects (2)
                              const sortedEntries = Array.from(summaryMap.entries())
                                .sort((a, b) => a[1].sortOrder - b[1].sortOrder);

                              const hasData = freeCount > 0 || sortedEntries.length > 0;

                              return (
                                <div
                                  key={`summary-${dayIndex}`}
                                  className={`border-r flex items-start justify-center min-h-16 py-1 ${
                                    isSunday ? 'border-red-400 bg-red-100 bg-opacity-40' : 'border-steel-200'
                                  } ${isToday ? 'ring-2 ring-blue-400 ring-inset bg-brand-50' : ''}`}
                                  style={{ width: dayWidth }}
                                >
                                  {hasData ? (
                                    <div className="flex flex-col items-center gap-0.5 pt-0.5">
                                      {/* Sum at top */}
                                      <div
                                        className="text-slate-700 font-bold text-xs rounded shadow-sm flex items-center justify-center bg-steel-300 border border-steel-400"
                                        style={{ minWidth: '24px', height: '18px' }}
                                        title={`Total: ${freeCount + sortedEntries.reduce((sum, [, data]) => sum + data.users.size, 0)} people`}
                                      >
                                        {freeCount + sortedEntries.reduce((sum, [, data]) => sum + data.users.size, 0)}
                                      </div>
                                      {/* Free people */}
                                      {freeCount > 0 && (
                                        <div
                                          className="text-white font-bold text-xs rounded shadow-sm cursor-help flex items-center justify-center"
                                          style={{ backgroundColor: '#22c55e', minWidth: '24px', height: '18px' }}
                                          title={`Available: ${freeCount} ${freeCount === 1 ? 'person' : 'people'}`}
                                        >
                                          {freeCount}
                                        </div>
                                      )}
                                      {/* Special tiles and projects */}
                                      {sortedEntries.map(([key, data]) => (
                                        <div
                                          key={`${dayIndex}-${key}`}
                                          className="text-white font-bold text-xs rounded shadow-sm cursor-help flex items-center justify-center"
                                          style={{ backgroundColor: data.color, minWidth: '24px', height: '18px' }}
                                          title={`${data.name}: ${data.users.size} ${data.users.size === 1 ? 'person' : 'people'}`}
                                        >
                                          {data.users.size}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-slate-400 text-sm font-light pt-4">–</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {(showCars || showEquipment) && (
                      <div>
                        <div className="min-h-16 bg-gradient-to-br from-blue-100 via-blue-50 to-blue-100 border-t-2 border-blue-500 border-l-2 border-l-blue-300 rounded-r" style={{ width: days.length * dayWidth }}>
                          <div className="relative flex">
                            {days.map((day, dayIndex) => {
                              // Group cars by project for this day
                              const projectCarsMap = new Map();

                              cars.forEach(car => {
                                const carAssignmentsForDay = carAssignments
                                  .filter(a => a.carId === car.id)
                                  .filter(a => {
                                    const assignmentStart = new Date(a.startDate);
                                    const assignmentEnd = new Date(a.endDate);
                                    const currentDay = new Date(day);
                                    assignmentStart.setHours(0, 0, 0, 0);
                                    assignmentEnd.setHours(0, 0, 0, 0);
                                    currentDay.setHours(0, 0, 0, 0);
                                    return currentDay >= assignmentStart && currentDay <= assignmentEnd;
                                  });

                                carAssignmentsForDay.forEach(a => {
                                  if (!projectCarsMap.has(a.projectId)) {
                                    projectCarsMap.set(a.projectId, new Set());
                                  }
                                  projectCarsMap.get(a.projectId).add(car.id);
                                });
                              });

                              const isSunday = day.getDay() === 0;
                              const isToday = day.toDateString() === new Date().toDateString();
                              const hasData = projectCarsMap.size > 0;

                              return (
                                <div
                                  key={`car-summary-${dayIndex}`}
                                  className={`border-r flex items-center justify-center min-h-16 py-2 ${
                                    isSunday ? 'border-red-400 bg-red-100 bg-opacity-40' : 'border-steel-200'
                                  } ${isToday ? 'ring-2 ring-blue-400 ring-inset bg-brand-50' : ''}`}
                                  style={{ width: dayWidth }}
                                >
                                  {hasData ? (
                                    <div className="flex flex-col items-center gap-0.5 py-1">
                                      {Array.from(projectCarsMap.entries()).map(([projectId, carIds]) => {
                                        const project = projects.find(p => p.id === projectId);
                                        const projectColor = project?.color || '#6B7280';
                                        const carsCount = carIds.size;

                                        return (
                                          <div
                                            key={`car-${dayIndex}-${projectId}`}
                                            className="text-white font-bold text-xs rounded px-1.5 py-0.5 shadow-sm cursor-help whitespace-nowrap"
                                            style={{ backgroundColor: projectColor }}
                                            title={`${project?.name || 'Unknown'}: ${carsCount} ${carsCount === 1 ? 'car' : 'cars'}`}
                                          >
                                            {carsCount}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div className="text-slate-400 text-sm font-light">–</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Conflicts Modal */}
      <ConflictsModal 
        isOpen={showConflictsModal}
        onClose={() => setShowConflictsModal(false)}
        conflicts={stats.conflictsData}
        users={users}
        projects={projects}
        onDeleteAssignment={onDeleteAssignment}
      />
    </div>
  );
};

export default StatusBar;